<template>
  <v-main class="personal-chat">
    <v-container fluid class="pa-0" style="height: 100vh;">
      <v-row no-gutters style="height: 100%;">
        
        <!-- Chat List Sidebar -->
        <v-col cols="12" md="4" lg="3" class="chat-sidebar">
          <div class="sidebar-header">
            <div class="header-content">
              <h2 class="chat-title">Personal Chats</h2>
              <v-spacer></v-spacer>
              <v-btn icon @click="showNewChatDialog = true">
                <v-icon color="primary">mdi-message-plus</v-icon>
              </v-btn>
            </div>
            
            <!-- Search Bar -->
            <v-text-field
              v-model="searchQuery"
              placeholder="Search chats..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              class="search-field mt-3"
            ></v-text-field>
          </div>

          <!-- Chat List -->
          <v-list class="chat-list" two-line>
            <v-list-item
              v-for="chat in filteredChats"
              :key="chat.id"
              @click="selectChat(chat)"
              :class="{ 'active-chat': selectedChat?.id === chat.id }"
              class="chat-item"
            >
              <v-list-item-avatar size="50">
                <v-avatar :color="chat.avatar_color" size="50">
                  <v-img v-if="chat.avatar" :src="chat.avatar"></v-img>
                  <span v-else class="white--text font-weight-bold text-h6">
                    {{ chat.name.charAt(0).toUpperCase() }}
                  </span>
                </v-avatar>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="chat-name">
                  {{ chat.name }}
                  <v-chip v-if="chat.unread_count > 0" x-small color="error" text-color="white" class="ml-2">
                    {{ chat.unread_count }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle class="last-message">
                  <v-icon v-if="chat.last_message?.sender_id === userId" small color="grey" class="mr-1">
                    {{ chat.last_message?.is_read ? 'mdi-check-all' : 'mdi-check' }}
                  </v-icon>
                  {{ chat.last_message?.message || 'No messages yet' }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <div class="message-time">
                  {{ formatChatTime(chat.last_message?.created_at) }}
                </div>
                <v-btn icon small v-if="chat.is_online" class="online-indicator">
                  <v-icon small color="success">mdi-circle</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>

        <!-- Chat Conversation Area -->
        <v-col cols="12" md="8" lg="9" class="chat-conversation">
          <div v-if="!selectedChat" class="no-chat-selected">
            <div class="text-center">
              <v-icon size="100" color="grey lighten-2">mdi-chat-outline</v-icon>
              <h3 class="grey--text mt-4">Select a chat to start messaging</h3>
              <p class="grey--text">Choose from your existing conversations or start a new one</p>
            </div>
          </div>

          <div v-else class="chat-area">
            <!-- Chat Header -->
            <div class="chat-header">
              <v-avatar :color="selectedChat.avatar_color" size="40" class="mr-3">
                <v-img v-if="selectedChat.avatar" :src="selectedChat.avatar"></v-img>
                <span v-else class="white--text font-weight-bold">
                  {{ selectedChat.name.charAt(0).toUpperCase() }}
                </span>
              </v-avatar>
              
              <div class="header-info">
                <h4 class="chat-header-name">{{ selectedChat.name }}</h4>
                <p class="chat-status">
                  <v-icon small :color="selectedChat.is_online ? 'success' : 'grey'" class="mr-1">
                    mdi-circle
                  </v-icon>
                  {{ selectedChat.is_online ? 'Online' : `Last seen ${formatChatTime(selectedChat.last_seen)}` }}
                </p>
              </div>

              <v-spacer></v-spacer>

              <div class="header-actions">
                <v-btn icon @click="startVideoCall">
                  <v-icon color="primary">mdi-video</v-icon>
                </v-btn>
                <v-btn icon @click="startVoiceCall">
                  <v-icon color="primary">mdi-phone</v-icon>
                </v-btn>
                <v-btn icon @click="showChatInfo = true">
                  <v-icon color="primary">mdi-information-outline</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Messages Area -->
            <div ref="messagesContainer" class="messages-container" @scroll="onScroll">
              <div class="messages-list">
                <div
                  v-for="message in selectedChatMessages"
                  :key="message.id"
                  :class="[
                    'message-wrapper',
                    message.sender_id === userId ? 'message-sent' : 'message-received'
                  ]"
                >
                  <div class="message-bubble">
                    <div v-if="message.type === 'text'" class="message-content">
                      {{ message.message }}
                    </div>
                    <div v-else-if="message.type === 'image'" class="message-image">
                      <v-img :src="message.file_url" max-width="300" class="rounded"></v-img>
                    </div>
                    <div v-else-if="message.type === 'file'" class="message-file">
                      <v-icon left>mdi-file-document</v-icon>
                      {{ message.file_name }}
                    </div>
                    
                    <div class="message-meta">
                      <span class="message-time">{{ formatTime(message.created_at) }}</span>
                      <v-icon 
                        v-if="message.sender_id === userId" 
                        small 
                        :color="message.is_read ? 'blue' : 'grey'"
                        class="ml-1"
                      >
                        {{ message.is_read ? 'mdi-check-all' : 'mdi-check' }}
                      </v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Message Input -->
            <div class="message-input-area">
              <div class="input-container">
                <v-btn icon @click="showEmojiPicker = !showEmojiPicker">
                  <v-icon color="grey">mdi-emoticon-happy-outline</v-icon>
                </v-btn>
                
                <v-text-field
                  v-model="newMessage"
                  placeholder="Type a message..."
                  outlined
                  dense
                  hide-details
                  class="message-input"
                  @keyup.enter="sendMessage"
                  @input="onTyping"
                ></v-text-field>

                <v-btn icon @click="$refs.fileInput.click()">
                  <v-icon color="grey">mdi-attachment</v-icon>
                </v-btn>
                
                <v-btn 
                  icon 
                  :disabled="!newMessage.trim()" 
                  @click="sendMessage"
                  :color="newMessage.trim() ? 'primary' : 'grey'"
                >
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </div>
              
              <!-- Typing Indicator -->
              <div v-if="isTyping" class="typing-indicator">
                <span class="typing-text">{{ selectedChat.name }} is typing</span>
                <div class="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Hidden File Input -->
    <input ref="fileInput" type="file" hidden @change="handleFileUpload" multiple>

    <!-- New Chat Dialog -->
    <v-dialog v-model="showNewChatDialog" max-width="400">
      <v-card>
        <v-card-title>Start New Chat</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedUsers"
            :items="availableUsers"
            item-text="name"
            item-value="id"
            label="Select users"
            multiple
            outlined
            dense
          >
            <template slot="selection" slot-scope="{ item }">
              <v-chip small>{{ item.name }}</v-chip>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showNewChatDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="createNewChat">Start Chat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Chat Info Dialog -->
    <v-dialog v-model="showChatInfo" max-width="400">
      <v-card v-if="selectedChat">
        <v-card-title>Chat Information</v-card-title>
        <v-card-text>
          <div class="text-center mb-4">
            <v-avatar :color="selectedChat.avatar_color" size="80">
              <v-img v-if="selectedChat.avatar" :src="selectedChat.avatar"></v-img>
              <span v-else class="white--text font-weight-bold text-h4">
                {{ selectedChat.name.charAt(0).toUpperCase() }}
              </span>
            </v-avatar>
            <h3 class="mt-2">{{ selectedChat.name }}</h3>
            <p class="grey--text">{{ selectedChat.email }}</p>
          </div>
          
          <v-list dense>
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="success">mdi-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Status</v-list-item-title>
                <v-list-item-subtitle>{{ selectedChat.is_online ? 'Online' : 'Offline' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            
            <v-list-item>
              <v-list-item-icon>
                <v-icon color="info">mdi-calendar</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Member Since</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(selectedChat.created_at) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showChatInfo = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import Echo from "@/plugins/echo";

export default {
  name: "PersonalChats",
  data() {
    return {
      // User and chat data
      userId: 1, // Mock user ID - replace with actual user from store
      searchQuery: "",
      selectedChat: null,
      newMessage: "",
      isTyping: false,
      typingTimer: null,
      
      // UI states
      showNewChatDialog: false,
      showChatInfo: false,
      showEmojiPicker: false,
      selectedUsers: [],
      
      // Mock chat data - replace with API calls
      chats: [
        {
          id: 1,
          name: "Sarah Ahmed",
          email: "sarah@example.com",
          avatar: null,
          avatar_color: "primary",
          is_online: true,
          last_seen: "2024-01-28T10:30:00Z",
          unread_count: 3,
          last_message: {
            id: 101,
            sender_id: 2,
            message: "Hey! How are you doing?",
            created_at: "2024-01-28T10:30:00Z",
            is_read: false
          },
          created_at: "2024-01-15T08:00:00Z"
        },
        {
          id: 2,
          name: "Ahmed Khan",
          email: "ahmed@example.com", 
          avatar: null,
          avatar_color: "success",
          is_online: false,
          last_seen: "2024-01-28T09:15:00Z",
          unread_count: 0,
          last_message: {
            id: 102,
            sender_id: 1,
            message: "Thanks for the help!",
            created_at: "2024-01-28T09:15:00Z",
            is_read: true
          },
          created_at: "2024-01-10T12:00:00Z"
        },
        {
          id: 3,
          name: "Maya Patel",
          email: "maya@example.com",
          avatar: null,
          avatar_color: "purple",
          is_online: true,
          last_seen: "2024-01-28T11:00:00Z",
          unread_count: 1,
          last_message: {
            id: 103,
            sender_id: 3,
            message: "Can we schedule a meeting?",
            created_at: "2024-01-28T08:45:00Z",
            is_read: false
          },
          created_at: "2024-01-05T15:30:00Z"
        },
        {
          id: 4,
          name: "John Smith",
          email: "john@example.com",
          avatar: null,
          avatar_color: "warning",
          is_online: false,
          last_seen: "2024-01-27T18:20:00Z",
          unread_count: 0,
          last_message: {
            id: 104,
            sender_id: 1,
            message: "Great work on the project!",
            created_at: "2024-01-27T17:30:00Z",
            is_read: true
          },
          created_at: "2024-01-20T10:00:00Z"
        }
      ],
      
      // Mock messages for selected chat
      chatMessages: {
        1: [
          {
            id: 1,
            sender_id: 2,
            receiver_id: 1,
            message: "Hi there!",
            type: "text",
            created_at: "2024-01-28T09:00:00Z",
            is_read: true
          },
          {
            id: 2,
            sender_id: 1,
            receiver_id: 2,
            message: "Hello! How can I help you?",
            type: "text",
            created_at: "2024-01-28T09:05:00Z",
            is_read: true
          },
          {
            id: 3,
            sender_id: 2,
            receiver_id: 1,
            message: "I need some information about the voice room features",
            type: "text",
            created_at: "2024-01-28T09:10:00Z",
            is_read: true
          },
          {
            id: 4,
            sender_id: 1,
            receiver_id: 2,
            message: "Sure! What would you like to know?",
            type: "text",
            created_at: "2024-01-28T09:12:00Z",
            is_read: false
          }
        ],
        2: [
          {
            id: 5,
            sender_id: 2,
            receiver_id: 1,
            message: "Thanks for your help yesterday!",
            type: "text",
            created_at: "2024-01-28T09:15:00Z",
            is_read: true
          }
        ],
        3: [
          {
            id: 6,
            sender_id: 3,
            receiver_id: 1,
            message: "Can we schedule a meeting for next week?",
            type: "text",
            created_at: "2024-01-28T08:45:00Z",
            is_read: false
          }
        ]
      },
      
      // Available users for new chats
      availableUsers: [
        { id: 5, name: "Emma Wilson" },
        { id: 6, name: "Ali Hassan" },
        { id: 7, name: "Lisa Chen" },
        { id: 8, name: "David Brown" }
      ]
    };
  },

  computed: {
    user() {
      // Replace with actual store getter
      return this.$store?.getters?.["auth/user"] || { id: 1, name: "Current User" };
    },
    
    filteredChats() {
      if (!this.searchQuery) return this.chats;
      
      return this.chats.filter(chat =>
        chat.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        chat.last_message?.message?.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    
    selectedChatMessages() {
      if (!this.selectedChat) return [];
      return this.chatMessages[this.selectedChat.id] || [];
    }
  },

  mounted() {
    this.initializeChat();
    this.setupEchoListeners();
  },

  beforeDestroy() {
    // Clean up timers
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }
  },

  methods: {
    initializeChat() {
      // Initialize with user data
      if (this.user) {
        this.userId = this.user.id;
      }
      
      // Auto-select first chat if available
      if (this.chats.length > 0) {
        this.selectChat(this.chats[0]);
      }
      
      // Load initial chat data
      this.loadChats();
    },

    setupEchoListeners() {
      if (!this.userId || !Echo) return;

      try {
        Echo.private(`chat.${this.userId}`)
          .listen(".MessageSent", (e) => {
            console.log("📩 New Message:", e.message);
            this.handleNewMessage(e.message);
          })
          .error((error) => {
            console.error("Echo error:", error);
          });

        console.log(`Listening to channel: chat.${this.userId}`);
      } catch (error) {
        console.error("Failed to setup Echo listeners:", error);
      }
    },

    async loadChats() {
      try {
        // Replace with actual API call
        // const response = await axios.get('/api/chats');
        // this.chats = response.data.data;
        console.log("Chats loaded");
      } catch (error) {
        console.error("Failed to load chats:", error);
      }
    },

    selectChat(chat) {
      this.selectedChat = chat;
      this.loadChatMessages(chat.id);
      this.markAsRead(chat.id);
      
      // Scroll to bottom of messages
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    async loadChatMessages(chatId) {
      try {
        // Replace with actual API call
        // const response = await axios.get(`/api/chats/${chatId}/messages`);
        // this.chatMessages[chatId] = response.data.data;
        console.log(`Loading messages for chat ${chatId}`);
      } catch (error) {
        console.error("Failed to load chat messages:", error);
      }
    },

    async sendMessage() {
      if (!this.newMessage.trim() || !this.selectedChat) return;

      const message = {
        id: Date.now(),
        sender_id: this.userId,
        receiver_id: this.selectedChat.id,
        message: this.newMessage.trim(),
        type: "text",
        created_at: new Date().toISOString(),
        is_read: false
      };

      try {
        // Add to UI immediately
        if (!this.chatMessages[this.selectedChat.id]) {
          this.chatMessages[this.selectedChat.id] = [];
        }
        this.chatMessages[this.selectedChat.id].push(message);
        
        // Update last message in chat list
        const chatIndex = this.chats.findIndex(c => c.id === this.selectedChat.id);
        if (chatIndex !== -1) {
          this.chats[chatIndex].last_message = {
            ...message,
            sender_id: this.userId
          };
        }

        this.newMessage = "";
        this.scrollToBottom();

        // Send to server - replace with actual API call
        /*
        await fetch("http://127.0.0.1:8000/api/send_messages", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            sender_id: this.userId,
            receiver_id: this.selectedChat.id,
            type: "private",
            message: message.message,
          }),
        });
        */

      } catch (error) {
        console.error("Failed to send message:", error);
        // Remove message from UI on failure
        const messages = this.chatMessages[this.selectedChat.id];
        const index = messages.findIndex(m => m.id === message.id);
        if (index !== -1) {
          messages.splice(index, 1);
        }
      }
    },

    handleNewMessage(message) {
      const chatId = message.sender_id === this.userId ? message.receiver_id : message.sender_id;
      
      if (!this.chatMessages[chatId]) {
        this.chatMessages[chatId] = [];
      }
      this.chatMessages[chatId].push(message);

      // Update chat list
      const chatIndex = this.chats.findIndex(c => c.id === chatId);
      if (chatIndex !== -1) {
        this.chats[chatIndex].last_message = message;
        if (message.sender_id !== this.userId) {
          this.chats[chatIndex].unread_count += 1;
        }
      }

      // Scroll if current chat
      if (this.selectedChat && this.selectedChat.id === chatId) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    onTyping() {
      // Implement typing indicator logic
      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
      }
      
      // Send typing indicator to other user
      this.typingTimer = setTimeout(() => {
        // Stop typing indicator
      }, 3000);
    },

    onScroll() {
      // Implement infinite scroll for message history
      const container = this.$refs.messagesContainer;
      if (container.scrollTop === 0) {
        // Load more messages
        this.loadMoreMessages();
      }
    },

    loadMoreMessages() {
      // Implement pagination for messages
      console.log("Loading more messages...");
    },

    markAsRead(chatId) {
      const chatIndex = this.chats.findIndex(c => c.id === chatId);
      if (chatIndex !== -1) {
        this.chats[chatIndex].unread_count = 0;
      }
      // Send read receipt to server
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    handleFileUpload(event) {
      const files = event.target.files;
      for (let file of files) {
        console.log("Uploading file:", file.name);
        // Implement file upload logic
      }
    },

    createNewChat() {
      if (this.selectedUsers.length === 0) return;
      
      // Create new chat with selected users
      console.log("Creating new chat with:", this.selectedUsers);
      this.showNewChatDialog = false;
      this.selectedUsers = [];
    },

    startVideoCall() {
      console.log("Starting video call with:", this.selectedChat.name);
      // Implement video call functionality
    },

    startVoiceCall() {
      console.log("Starting voice call with:", this.selectedChat.name);
      // Implement voice call functionality
    },

    formatTime(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatChatTime(date) {
      if (!date) return "";
      const d = new Date(date);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "now";
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays < 7) return `${diffDays}d`;
      
      return d.toLocaleDateString();
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.personal-chat {
  background: #f8f9fa;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

/* Chat Sidebar */
.chat-sidebar {
  background: white;
  border-right: 1px solid #e0e0e0;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 20px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.search-field {
  border-radius: 12px;
}

.search-field >>> .v-input__control {
  min-height: 40px;
}

/* Chat List */
.chat-list {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.chat-item {
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s ease;
  cursor: pointer;
  padding: 12px 16px;
}

.chat-item:hover {
  background: #f8f9fa;
}

.chat-item.active-chat {
  background: #e3f2fd;
  border-right: 3px solid #2196f3;
}

.chat-name {
  font-weight: 500;
  font-size: 1rem;
  color: #1a1a1a;
  display: flex;
  align-items: center;
}

.last-message {
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  margin-top: 2px;
}

.message-time {
  font-size: 0.75rem;
  color: #999;
  margin-bottom: 4px;
}

.online-indicator {
  margin-top: 4px;
}

/* Chat Conversation */
.chat-conversation {
  background: white;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.no-chat-selected {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}

.chat-area {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Chat Header */
.chat-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.header-info {
  flex: 1;
}

.chat-header-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 4px 0;
}

.chat-status {
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 8px;
}

/* Messages Container */
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  background: #f8f9fa;
  background-image: 
    radial-gradient(circle at 25px 25px, rgba(255,255,255,0.8) 2px, transparent 0),
    radial-gradient(circle at 75px 75px, rgba(255,255,255,0.8) 2px, transparent 0);
  background-size: 100px 100px;
}

.messages-list {
  padding: 20px;
  min-height: 100%;
}

/* Messages */
.message-wrapper {
  margin-bottom: 12px;
  display: flex;
}

.message-wrapper.message-sent {
  justify-content: flex-end;
}

.message-wrapper.message-received {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 70%;
  min-width: 100px;
  border-radius: 16px;
  padding: 12px 16px 8px 16px;
  position: relative;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.message-sent .message-bubble {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-received .message-bubble {
  background: white;
  color: #1a1a1a;
  border-bottom-left-radius: 4px;
  border: 1px solid #e0e0e0;
}

.message-content {
  font-size: 0.95rem;
  line-height: 1.4;
  word-wrap: break-word;
}

.message-image {
  margin-bottom: 4px;
}

.message-file {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.message-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 4px;
  gap: 4px;
}

.message-time {
  font-size: 0.75rem;
  opacity: 0.7;
}

.message-sent .message-time {
  color: rgba(255,255,255,0.8);
}

.message-received .message-time {
  color: #999;
}

/* Message Input */
.message-input-area {
  background: white;
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
}

.input-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-input {
  flex: 1;
}

.message-input >>> .v-input__control {
  min-height: 44px;
}

.message-input >>> .v-text-field__details {
  display: none;
}

.message-input >>> .v-input__slot {
  border-radius: 20px !important;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.85rem;
  color: #666;
}

.typing-text {
  font-style: italic;
}

.typing-dots {
  display: flex;
  gap: 2px;
}

.typing-dots span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #666;
  animation: typing-pulse 1.4s infinite ease-in-out;
}

.typing-dots span:nth-child(1) { animation-delay: -0.32s; }
.typing-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing-pulse {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Scrollbar Styling */
.messages-container::-webkit-scrollbar,
.chat-list::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track,
.chat-list::-webkit-scrollbar-track {
  background: transparent;
}

.messages-container::-webkit-scrollbar-thumb,
.chat-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover,
.chat-list::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

/* Dialog Styling */
.v-dialog >>> .v-card {
  border-radius: 16px !important;
}

.v-dialog >>> .v-card-title {
  font-weight: 600;
  font-size: 1.2rem;
  color: #1a1a1a;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .chat-sidebar {
    position: absolute;
    z-index: 10;
    width: 100%;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
  
  .chat-sidebar.mobile-open {
    transform: translateX(0);
  }
  
  .sidebar-header {
    padding: 16px;
  }
  
  .chat-title {
    font-size: 1.3rem;
  }
  
  .messages-list {
    padding: 16px;
  }
  
  .message-bubble {
    max-width: 85%;
  }
  
  .chat-header {
    padding: 12px 16px;
  }
  
  .message-input-area {
    padding: 12px 16px;
  }
}

/* Animation for new messages */
.message-wrapper {
  animation: messageSlideIn 0.3s ease-out;
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Unread message indicator */
.chat-item >>> .v-chip {
  min-width: 20px;
  height: 20px;
  font-size: 0.7rem;
  font-weight: 600;
}

/* Online status styling */
.v-list-item-action .v-icon {
  font-size: 12px !important;
}

/* Input field focus styling */
.message-input >>> .v-input--is-focused .v-input__slot {
  border-color: #2196f3 !important;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

/* Button hover effects */
.v-btn:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

/* List item avatar sizing */
.v-list-item-avatar {
  margin-right: 12px !important;
}

/* Search field styling */
.search-field >>> .v-input__slot::before {
  border-color: transparent;
}

.search-field >>> .v-input__slot::after {
  border-color: transparent;
}

/* Card elevation for dialogs */
.v-card {
  box-shadow: 0 8px 32px rgba(0,0,0,0.12) !important;
}
</style>
